import React from 'react';
import { GameCard } from '../../../Games/GameCard';
import { GameCardContainerType } from '../../../Games/GameCard/GameCard.types';
import { RowCollectionLayout } from '../layouts/types';
import { Game, GameItemResponse, GameChannel } from '../../../Games/games.model';
import { transformGameData } from '../../../../utils/games/games.utils';
import { getImageUrl } from '../../../../utils/navigation';
import { ItvCollectionType } from '../../types';
import { GetBingoPageDataQuery } from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-bingo-page-data.query.generated';
import { GetSpinsPageDataQuery } from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-spins-page-data.query.generated';
import { PromotionCard } from '../../../Layout/Promotions/PromotionCard';
import { APP_URLS } from '../../../../consts';
import { PlatformPromos } from '../../../../hooks/useFetchPlatformPromos/types';
import { evaluatePromosWithPlatform, isAnyPlatformPromoReceived } from './platformPromos';

type PageDataWithCarousel = GetBingoPageDataQuery['pageData'][number] | GetSpinsPageDataQuery['pageData'][number];
type CarouselCollections = NonNullable<NonNullable<PageDataWithCarousel['collections']>[number]>;
type CollectionItem = NonNullable<CarouselCollections['Collections_id']>;
type PromoItems = CollectionItem['promotions'];
type PromoItem = NonNullable<NonNullable<NonNullable<PromoItems>[number]>['magicx_promotions_id']>;
type CollectionGameEntry = {
  game_id?: GameItemResponse | null;
} | null;

export type CollectionGames = Array<CollectionGameEntry> | null;
type BuildTilesInput = {
  collectionId?: string;
  collectionType?: string | null;
  layoutType?: RowCollectionLayout;
  games?: CollectionGames;
  promotions?: PromoItems;
  fallbackImages: string[];
  stubImageClassName?: string;
  platformPromos?: PlatformPromos;
};

const layoutContainerPattern: Partial<Record<RowCollectionLayout, GameCardContainerType[]>> = {
  'hero-1': [GameCardContainerType.Wide],
  'hero-2': [GameCardContainerType.Wide, GameCardContainerType.Wide],
  'row-3': [GameCardContainerType.Wide, GameCardContainerType.Wide, GameCardContainerType.Wide],
  'row-5': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'row-6': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'grid-6': [
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
    GameCardContainerType.Wide,
  ],
  'grid-2x2': [
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'grid-5x5': [GameCardContainerType.Box],
  'wide+grid3x3': [
    GameCardContainerType.Wide,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
  'tall+grid2x2': [
    GameCardContainerType.Tall,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Tall,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
    GameCardContainerType.Box,
  ],
};

const getContainerTypeForIndex = (
  layoutType: RowCollectionLayout | undefined,
  index: number
): GameCardContainerType => {
  const pattern = layoutType ? layoutContainerPattern[layoutType] : null;

  if (!pattern || pattern.length === 0) return GameCardContainerType.Box;

  const result = pattern[Math.min(index, pattern.length - 1)] ?? GameCardContainerType.Box;

  console.log(index, result);

  return result;
};

const createMockGame = (imageUrl: string, index: number): Game => {
  const displayName = `Featured game ${index + 1}`;
  const friendlyUrlName = `featured-game-${index + 1}`;

  return {
    id: `mock-game-${index}`,
    gameId: `mock-game-${index}`,
    displayName,
    friendlyUrlName,
    categoryName: 'itv-collection',
    shortDescription: '',
    longDescription: '',
    rules: '',
    bannerImage: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameplayBackground: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImage: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageBox: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageTall: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    gameImageWide: { __typename: 'directus_files', imgUrl: imageUrl, width: null, height: null },
    channels: [
      {
        name: 'Desktop',
        channel: GameChannel.Desktop,
        width: 16,
        height: 9,
        isAvailableForDemoPlay: false,
      },
      {
        name: 'Mobile',
        channel: GameChannel.Mobile,
        width: 9,
        height: 16,
        isAvailableForDemoPlay: false,
      },
    ],
    provider: { mediatorId: '' },
    features: [],
    tags: [],
    rating: null,
    rtp: null,
    paylines: null,
    maxBet: null,
    minBet: null,
    maxRtp: null,
    minRtp: null,
    netPosTransactionCount: null,
    netPosDelay: null,
  };
};

const normalizeGame = (gameNode: GameItemResponse | null | undefined, fallbackImage: string, index: number): Game => {
  const transformedGame = transformGameData(gameNode);

  if (transformedGame) {
    return transformedGame;
  }

  return createMockGame(fallbackImage, index);
};

const renderGameTile = (
  game: Game,
  layoutType: RowCollectionLayout | undefined,
  index: number,
  keyPrefix: string,
  fallbackImage: string,
  containerWidth?: number
): JSX.Element => (
  <GameCard
    key={`${keyPrefix}-game-${game.id}-${index}`}
    game={game}
    containerType={getContainerTypeForIndex(layoutType, index)}
    fallbackImageAltText={game.displayName}
    fallbackImageUrl={fallbackImage}
    showGameName={false}
    disableMenu
    containerElementWidth={containerWidth}
    gameUrl={game.friendlyUrlName}
  />
);

const buildGameCardTiles = ({ collectionId, layoutType, games, fallbackImages }: BuildTilesInput): JSX.Element[] => {
  const safeFallbacks = fallbackImages.length ? fallbackImages : [''];
  const keyPrefix = collectionId ?? 'collection';
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth : undefined;

  if (!games?.length) {
    return safeFallbacks.map((src, index) =>
      renderGameTile(createMockGame(src, index), layoutType, index, keyPrefix, src, containerWidth)
    );
  }

  return games.map((entry, index) => {
    const fallbackImage = safeFallbacks[index % safeFallbacks.length] || '';
    const normalizedGame = normalizeGame(entry?.game_id, fallbackImage, index);

    return renderGameTile(normalizedGame, layoutType, index, keyPrefix, fallbackImage, containerWidth);
  });
};

const buildPromoCardTiles = ({ promotions, platformPromos, fallbackImages }: BuildTilesInput): (JSX.Element | null)[] =>
  (promotions || [])?.map((cmsPromo) => {
    const promo = cmsPromo?.magicx_promotions_id as PromoItem;
    const isPlatformPromoReceived = isAnyPlatformPromoReceived(platformPromos);

    const parsedPromo = {
      id: promo.id,
      startDate: `${promo.start_date}T${promo.start_time || '00:00:00'}`,
      endDate: `${promo.end_date}T${promo.end_time || '23:59:59'}`,
      bonusType: String(promo.__typename),
      title: String(promo.translations?.[0]?.title),
      subtitle: String(promo.translations?.[0]?.subtitle || ''),
      description: String(promo.translations?.[0]?.description),
      ctaCopy: promo.translations?.[0]?.cta_copy,
      ctaUrl: String(promo.translations?.[0]?.cta_url),
      bonusId: promo.bonus_ID,
      tagId: promo.tag_ID,
      promoType: promo.bonus_type?.toLowerCase(),
      dmBonusId: promo.DM_Template_ID,
      dmMaxAmount: promo.DM_Max_Amount,
      imgPath: getImageUrl(promo.thumbnail?.filename_disk) || fallbackImages[0],
      wideImgPath: getImageUrl(promo.thumb_wide_desktop?.filename_disk),
      imgAlt: String(promo.reg_image_desktop?.title),
      pageUrl: `${APP_URLS.itv.promotionRoot}/${promo.translations?.[0]?.slug}`,
      successCTAGameInformation: {
        gameId: '',
      },
      magicXSlug: promo.magicx_slug,
    };

    const compiledPromo = isPlatformPromoReceived
      ? evaluatePromosWithPlatform(parsedPromo, platformPromos as PlatformPromos)
      : parsedPromo;

    return compiledPromo ? <PromotionCard key={promo.id} promo={compiledPromo} /> : null;
  });

const buildStubTiles = ({
  collectionId,
  fallbackImages,
  stubImageClassName,
}: Pick<BuildTilesInput, 'collectionId' | 'fallbackImages' | 'stubImageClassName'>): JSX.Element[] => {
  const className = stubImageClassName ?? 'itv-row-collection__stub-image';
  const keyPrefix = collectionId ?? 'collection';

  return fallbackImages.map((src, index) => (
    <img key={`${keyPrefix}-stub-${index}`} src={src} alt={`Collection item ${index + 1}`} className={className} />
  ));
};

export const buildCollectionTiles = (options: BuildTilesInput): (JSX.Element | null)[] => {
  const { collectionType } = options;

  if (collectionType === ItvCollectionType.GAMES) {
    return buildGameCardTiles(options);
  }

  if (collectionType === ItvCollectionType.PROMOTIONS) {
    return buildPromoCardTiles(options);
  }

  return buildStubTiles(options);
};
